import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

interface Ticket {
  id: number;
  ticket_id: string;
  company_id: number;
  company_name: string;
  company_email: string;
  user_id?: number;
  user_email?: string;
  created_by?: number;
  created_by_email?: string;
  last_updated_by?: number;
  last_updated_by_email?: string;
  last_updated_by_company_id?: number;
  assigned_to?: number;
  assigned_to_email?: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
}

interface TicketReply {
  id: number;
  ticket_id: number;
  user_id: number;
  user_email: string;
  user_role: string;
  user_company_id?: number;
  message: string;
  type: 'reply' | 'agent_reply' | 'internal';
  created_at: string;
}

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyType, setReplyType] = useState<'reply' | 'agent_reply' | 'internal'>('agent_reply');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isGlobalAdmin = user?.role === 'admin' && !user?.company_id;
  const isGlobalUser = !user?.company_id;
  const isAgent = isGlobalAdmin || isGlobalUser;
  const isCompanyAdmin = user?.role === 'admin' && user?.company_id;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    if (id) {
      fetchTicket();
      fetchReplies();
    }
  }, [id]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
    } catch (error: any) {
      console.error('Error fetching ticket:', error);
      if (error.response?.status === 404) {
        setError('Ticket not found');
      } else if (error.response?.status === 403) {
        setError('Access denied');
      } else {
        setError('Error loading ticket');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/tickets/${id}/replies`);
      setReplies(response.data);
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  const handleAddReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !replyMessage.trim()) return;

    try {
      const payload: any = {
        message: replyMessage,
      };
      
      if (isAgent) {
        payload.type = replyType;
      } else {
        payload.type = 'reply';
      }

      await api.post(`/tickets/${ticket.id}/replies`, payload);
      setReplyMessage('');
      if (isAgent) {
        setReplyType('agent_reply');
      }
      fetchReplies();
      fetchTicket(); // Refresh ticket to update updated_at
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding reply');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Ticket not found'}</p>
          <Link
            to="/tickets"
            className="mt-4 inline-block text-sm text-indigo-600 hover:text-indigo-900"
          >
            ← Back to Tickets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <Link
          to="/tickets"
          className="text-sm text-indigo-600 hover:text-indigo-900 mb-4 inline-block"
        >
          ← Back to Tickets
        </Link>
        <div className="flex justify-between items-start mt-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {ticket.subject}
            </h1>
            <div className="mt-2 flex items-center space-x-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ticket ID: <span className="font-mono font-semibold">{ticket.ticket_id || `TKT-${String(ticket.id).padStart(6, '0')}`}</span>
              </p>
              <button
                onClick={() => copyToClipboard(ticket.ticket_id || `TKT-${String(ticket.id).padStart(6, '0')}`)}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-indigo-600 hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title="Copy Ticket ID to clipboard"
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ticket.status)}`}>
              {ticket.status.replace('_', ' ')}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
              {ticket.priority}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {(isGlobalAdmin || isGlobalUser) && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Company</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ticket.company_name} ({ticket.company_email})</p>
            </div>
          )}
          {ticket.user_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ticket.user_email}</p>
            </div>
          )}
          {ticket.created_by_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created By</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ticket.created_by_email}</p>
            </div>
          )}
          {ticket.last_updated_by_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Updated By</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {ticket.last_updated_by_company_id === null || ticket.last_updated_by_company_id === undefined
                  ? 'Agent'
                  : 'Customer'} ({ticket.last_updated_by_email})
              </p>
            </div>
          )}
          {ticket.assigned_to_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Assigned To</label>
              <p className="mt-1 text-sm text-gray-900">{ticket.assigned_to_email}</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{new Date(ticket.created_at).toLocaleString()}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Updated</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{new Date(ticket.updated_at).toLocaleString()}</p>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</label>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{ticket.description}</p>
        </div>
      </div>

      {/* Replies Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Replies</h2>
        <div className="space-y-4 mb-6">
          {replies.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No replies yet</p>
          ) : (
            replies.map((reply) => {
              const isInternal = reply.type === 'internal';
              const isAgentReply = reply.type === 'agent_reply';
              const isCustomerReply = reply.type === 'reply';

              return (
                <div
                  key={reply.id}
                  className={`p-4 rounded-lg border ${
                    isInternal
                      ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
                      : isAgentReply
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium ${
                        isInternal
                          ? 'text-gray-900 dark:text-yellow-100'
                          : isAgentReply
                          ? 'text-gray-900 dark:text-blue-100'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {reply.user_email}
                      </span>
                      {isInternal && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100">
                          Internal Note
                        </span>
                      )}
                      {isAgentReply && !isInternal && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100">
                          Agent
                        </span>
                      )}
                      {isCustomerReply && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                          Customer
                        </span>
                      )}
                    </div>
                    <span className={`text-xs ${
                      isInternal
                        ? 'text-gray-600 dark:text-yellow-200'
                        : isAgentReply
                        ? 'text-gray-600 dark:text-blue-200'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {new Date(reply.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className={`text-sm whitespace-pre-wrap ${
                    isInternal
                      ? 'text-gray-800 dark:text-yellow-50'
                      : isAgentReply
                      ? 'text-gray-800 dark:text-blue-50'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>{reply.message}</p>
                </div>
              );
            })
          )}
        </div>

        {/* Add Reply Form */}
        <form onSubmit={handleAddReply} className="border-t dark:border-gray-700 pt-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {isAgent ? 'Add Reply or Internal Note' : 'Add Reply'}
            </label>
            {isAgent && (
              <div className="mb-2">
                <select
                  value={replyType}
                  onChange={(e) => setReplyType(e.target.value as 'reply' | 'agent_reply' | 'internal')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="agent_reply">Agent Reply (Visible to Customer)</option>
                  <option value="internal">Internal Note (Agents Only)</option>
                </select>
              </div>
            )}
            <textarea
              required
              rows={4}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your reply..."
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {replyType === 'internal' ? 'Add Internal Note' : 'Add Reply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketDetail;
